import { useCallback } from 'react';

import { SAMPLE_SIZE_MAX, SAMPLE_SIZE_MIN } from '../constants';
import { InsightsJobFormData } from '../types';
import {
  validateAttributes,
  validateName,
  validatePartitions,
  validateSummaryPrompt,
  validateTimeRange,
} from '../utils/validations';

export function useValidateForm({
  formData,
  setErrors,
}: {
  formData?: InsightsJobFormData;
  setErrors?: (
    errors: Partial<Record<keyof InsightsJobFormData, string>>
  ) => void;
}) {
  const validateForm = useCallback(() => {
    if (!formData) return true;

    const newErrors: Partial<Record<keyof InsightsJobFormData, string>> = {};

    const timeRangeError = validateTimeRange(formData.timeRange);
    if (timeRangeError) {
      newErrors.timeRange = timeRangeError;
    }

    if (
      !formData.sample ||
      formData.sample <= SAMPLE_SIZE_MIN ||
      formData.sample > SAMPLE_SIZE_MAX
    ) {
      newErrors.sample = `Sample size must be between ${SAMPLE_SIZE_MIN} and ${SAMPLE_SIZE_MAX}`;
    }

    // Validate partitions using helper function
    const partitionsError = validatePartitions(formData.partitionPairs);
    if (partitionsError) {
      newErrors.partitionPairs = partitionsError;
    }

    // Validate summary prompt contains mustache variables
    const summaryPromptError = validateSummaryPrompt(formData.summaryPrompt);
    if (summaryPromptError) {
      newErrors.summaryPrompt = summaryPromptError;
    }

    // Validate attributes
    const attributesError = validateAttributes(formData.attributes || []);
    if (attributesError) {
      newErrors.attributes = attributesError;
    }

    // Validate name using helper function
    const nameError = validateName(formData.name || '');
    if (nameError) {
      newErrors.name = nameError;
    }

    setErrors?.(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  return { validateForm };
}
